import _ from 'underscore';
import { parseUrl } from '@bingads-webui-campaign-react/url-editor';
import { getFinalUrlWithPath } from '@bingads-webui-campaign-react/validated-url-input';

import {
  UAPPlacementTypeAlias as EPT,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';

const DEFAULT_URL_PREFIX = 'https';
const URL_COLON_SLASH_SLASH = '://';

const vmImagesFieldString = 'Images';
const vmImagesDataFieldString = 'ImageList';
const vmImageAssetsFieldString = 'ImageAssets';

const vmHeadlinesFieldString = 'Headlines';
const vmLongHeadlinesFieldString = 'LongHeadlines';
const vmDescriptionsFieldString = 'Descriptions';
const vmBusinessNameFieldString = 'BusinessName';
const vmCallToActionFieldString = 'CallToAction';
const vmFinalUrlsFieldString = 'FinalUrls';
const vmPath1FieldString = 'Path1';
const vmPath2FieldString = 'Path2';

export const pmaxRequiredAspectRatios = { Image191x100: 1, Image1x1: 1 };

// ignore Image169x100 because the minimum size (703x372) is bigger than original image 703x368
export const audienceMMAV2RequiredAspectRatios = {
  OriginalImage: 1, Image201x100: 1, Image200x100: 1, Image191x100: 1, Image169x100: 1, Image153x100: 1, Image124x100: 1, Image120x100: 1, Image1x1: 1,
};
export const specialMinimumSizeReqiurementAspectRatios = { Image169x100: [703, 372] };

export const getImagesFromImagesVm = vmImages => _.result(vmImages, vmImagesDataFieldString, undefined);
const getImageAssetsFromImagesVm = vmImages => _.result(vmImages, vmImageAssetsFieldString, []);

export const hasImages = imagesVm => _.result(getImagesFromImagesVm(imagesVm), 'length', 0) > 0;

export const getImageAssetsFromVm = (assetGroupVm, imageListPropName) => {
  const vmImages = _.result(assetGroupVm, imageListPropName || vmImagesFieldString, []);
  return getImageAssetsFromImagesVm(vmImages);
};

export const getImageListFromVm = (assetGroupVm, imageListPropName) => {
  const vmImages = _.result(assetGroupVm, imageListPropName || vmImagesFieldString, {});
  return getImagesFromImagesVm(vmImages);
};

export const createImagesVm = ({
  imageList,
  imageListPropName,
}) => (
  imageListPropName && !imageList ? {} : {
    [imageListPropName || vmImagesFieldString]: {
      [vmImagesDataFieldString]: imageList,
      [vmImageAssetsFieldString]: null,
    },
  });

export const getInternalFinalUrlStateFromUrl = (url) => {
  const finalUrlProtocolAndDest = parseUrl(url);

  return {
    internalFinalUrl: url,
    internalFinalUrlProtocol: `${finalUrlProtocolAndDest.protocol || DEFAULT_URL_PREFIX}${URL_COLON_SLASH_SLASH}`,
    internalFinalUrlDest: finalUrlProtocolAndDest.url,
  };
};

const getFirstNonEmptyItemKey = (items) => {
  const unEmptyItems = _.pick(items, _.identity);
  const keys = _.keys(unEmptyItems).sort();
  return _.first(keys);
};
const getFirstNonEmptyItem = (items) => {
  const key = getFirstNonEmptyItemKey(items);
  return key ? items[key] : undefined;
};

export const getAssetsFromVm = (assetGroupVm) => {
  const finalUrls = _.result(assetGroupVm, vmFinalUrlsFieldString, []);

  return ({
    shortHeadline: getFirstNonEmptyItem(_.result(assetGroupVm, vmHeadlinesFieldString, {})),
    longHeadline: getFirstNonEmptyItem(_.result(assetGroupVm, vmLongHeadlinesFieldString, {})),
    description: getFirstNonEmptyItem(_.result(assetGroupVm, vmDescriptionsFieldString, {})),
    businessName: _.result(assetGroupVm, vmBusinessNameFieldString, '') || '',
    callToAction: _.result(assetGroupVm, vmCallToActionFieldString, ''),
    finalUrl: _.first(finalUrls),
    displayUrl: getFinalUrlWithPath(finalUrls, _.result(assetGroupVm, vmPath1FieldString), _.result(assetGroupVm, vmPath2FieldString)),
  });
};

export const adPreviewConfig = [{
  placementSetName: 'Msn',
  placementTypes: [
    EPT.PMax.AudienceWideImage,
    EPT.PMax.MSEdgeSingleCarousel,
    EPT.PMax.MSNInArticle,
    EPT.PMax.AudienceSquareImage,
    EPT.PMax.MSNSmallHeadline,
    EPT.PMax.MMAWideImage,
    EPT.PMax.MMAVerticalImage,
  ],
}, {
  placementSetName: 'Bing',
  placementTypes: [
    EPT.PMax.MMALandscapeImage,
    EPT.BingSearch.Text,
  ],
}, {
  placementSetName: 'Edge',
  placementTypes: [
    EPT.PMax.MSEdgeSingleCarousel,
    EPT.PMax.MMALandscapeImage,
  ],
}, {
  placementSetName: 'Outlook',
  placementTypes: [
    EPT.Outlook.Mobile, EPT.Outlook.Web,
  ],
}];

export const adPreviewConfigV2 = [{
  placementSetName: 'Msn',
  placementTypes: [
    EPT.PMax.AudienceWideImage,
    EPT.PMax.AudienceSquareImage,
    EPT.PMax.MMAWideImage,
    EPT.PMax.MMAVerticalImage,
    EPT.PMax.MMAV2MSNSingleCarousel,
    EPT.PMax.MMAV2MSNRestoday,
    EPT.PMax.MMAV2MSNPartnerStripe,
  ],
}, {
  placementSetName: 'Bing',
  placementTypes: [
    EPT.PMax.MMALandscapeImage,
    EPT.BingSearch.Text,
  ],
}, {
  placementSetName: 'Edge',
  placementTypes: [
    EPT.PMax.MMALandscapeImage,
    EPT.PMax.MMAV2MSEdgeCarousel,
  ],
}, {
  placementSetName: 'Outlook',
  placementTypes: [
    EPT.Outlook.Mobile, EPT.Outlook.Web,
  ],
}];

export const adPreviewConfigV3 = [{
  placementSetName: 'Msn',
  placementTypes: [
    EPT.PMax.MMAV2MSNRiver,
    EPT.PMax.MSEdgeMobile,
    EPT.PMax.MMAWideImage,
    EPT.PMax.MMAVerticalImage,
    EPT.PMax.MMAV2MSNSingleCarousel,
    EPT.PMax.MMAV2MSNRestoday,
    EPT.PMax.MMAV2MSNPartnerStripe,
    EPT.PMax.MMAV2MSEdgeCarousel,
  ],
}, {
  placementSetName: 'Bing',
  placementTypes: [
    EPT.PMax.MMALandscapeImage,
    EPT.BingSearch.Text,
  ],
}, {
  placementSetName: 'Outlook',
  placementTypes: [
    EPT.Outlook.Mobile, EPT.Outlook.Web,
  ],
}];

export const AdPreviewSurface = {
  Msn: 'Msn',
  Bing: 'Bing',
  Edge: 'Edge',
  Outlook: 'Outlook',
  VideoGaming: 'VideoGaming',
};

export const AdPreviewSurfacePlacementKeys = {
  [AdPreviewSurface.Msn]: ['msan_191x100', 'msan_1x1', 'edge_178x100', 'msan_150x100', 'msan_133x100', 'mma_4x1', 'mma_1x2'],
  [AdPreviewSurface.Bing]: ['mma_191x100', 'bing_text'],
  [AdPreviewSurface.Edge]: ['mma_191x100', 'edge_178x100'],
  [AdPreviewSurface.Outlook]: ['outlook_text_mobile', 'outlook_text_web'],
};

export const AdPreviewSurfacePlacementKeysV2 = {
  [AdPreviewSurface.Msn]: ['msan_191x100', 'msan_1x1', 'mma_4x1', 'mma_1x2', 'mma_169x100', 'mma_124x100', 'mma_153x100'],
  [AdPreviewSurface.Bing]: ['mma_191x100', 'bing_text'],
  [AdPreviewSurface.Edge]: ['mma_191x100', 'edge_201x100'],
  [AdPreviewSurface.Outlook]: ['outlook_text_mobile', 'outlook_text_web'],
};

export const AdPreviewSurfacePlacementKeysV3 = {
  [AdPreviewSurface.Msn]: ['msan_191x100', 'msan_1x1', 'mma_4x1', 'mma_1x2', 'mma_169x100', 'mma_124x100', 'mma_153x100', 'edge_201x100'],
  [AdPreviewSurface.Bing]: ['mma_191x100', 'bing_text'],
  [AdPreviewSurface.Outlook]: ['outlook_text_mobile', 'outlook_text_web'],
};

export const getNumberOfPlacementBySurface = surface =>
  AdPreviewSurfacePlacementKeys[surface].length;

export const getNumberOfPlacementByCustomSurface = (surface, customSurfaceKeys) => customSurfaceKeys[surface] && customSurfaceKeys[surface].placements.length;
export const getNumberOfPlacementBySurfaceV2 = surface =>
  AdPreviewSurfacePlacementKeysV2[surface].length;

export const getNumberOfPlacementBySurfaceV3 = surface =>
  AdPreviewSurfacePlacementKeysV3[surface].length;

export const AdPreviewOverideStylesByKey = {
  mma_191x100: { imagePreviewHeight: 200, imagePreviewWidth: 382 },
  edge_178x100: { imagePreviewHeight: 220, imagePreviewWidth: 390 },
  msan_191x100: { imagePreviewHeight: 200, imagePreviewWidth: 382 },
  msan_150x100: { imagePreviewHeight: 200, imagePreviewWidth: 300 },
  mma_1x2: { imagePreviewHeight: 320, imagePreviewWidth: 160 },
};


export const responsiveAdPreviewConfig = [{
  placementSetName: AdPreviewSurface.Msn,
  placementTypes: [
    EPT.Responsive.PMax.AudienceWideImage,
    EPT.Responsive.PMax.MSEdgeSingleCarousel,
    EPT.Responsive.PMax.MSNInArticle,
    EPT.Responsive.PMax.AudienceSquareImage,
    EPT.Responsive.PMax.MSNSmallHeadline,
    EPT.Responsive.PMax.MMAWideImage,
    EPT.Responsive.PMax.MMAVerticalImage,
  ],
}, {
  placementSetName: AdPreviewSurface.Bing,
  placementTypes: [
    EPT.Responsive.PMax.MMALandscapeImage,
    EPT.Responsive.BingSearch.Text,
  ],
}, {
  placementSetName: AdPreviewSurface.Edge,
  placementTypes: [
    EPT.Responsive.PMax.MSEdgeSingleCarousel,
    EPT.Responsive.PMax.MMALandscapeImage,
  ],
}, {
  placementSetName: AdPreviewSurface.Outlook,
  placementTypes: [
    EPT.Responsive.Outlook.Mobile, EPT.Responsive.Outlook.Web,
  ],
}];

export const responsiveAdPreviewConfigV2 = [{
  placementSetName: AdPreviewSurface.Msn,
  placementTypes: [
    EPT.Responsive.PMax.AudienceWideImage,
    EPT.Responsive.PMax.AudienceSquareImage,
    EPT.Responsive.PMax.MMAWideImage,
    EPT.Responsive.PMax.MMAVerticalImage,
    EPT.Responsive.PMax.MMAV2MSNSingleCarousel,
    EPT.Responsive.PMax.MMAV2MSNRestoday,
    EPT.Responsive.PMax.MMAV2MSNPartnerStripe,
  ],
}, {
  placementSetName: AdPreviewSurface.Bing,
  placementTypes: [
    EPT.Responsive.PMax.MMALandscapeImage,
    EPT.Responsive.BingSearch.Text,
  ],
}, {
  placementSetName: AdPreviewSurface.Edge,
  placementTypes: [
    EPT.Responsive.PMax.MMALandscapeImage,
    EPT.Responsive.PMax.MMAV2MSEdgeCarousel,
  ],
}, {
  placementSetName: AdPreviewSurface.Outlook,
  placementTypes: [
    EPT.Responsive.Outlook.Mobile, EPT.Responsive.Outlook.Web,
  ],
}];

export const responsiveAdPreviewConfigV3 = [{
  placementSetName: AdPreviewSurface.Msn,
  placementTypes: [
    EPT.Responsive.PMax.MMAV2MSNRiver,
    EPT.Responsive.PMax.MSEdgeMobile,
    EPT.Responsive.PMax.MMAWideImage,
    EPT.Responsive.PMax.MMAVerticalImage,
    EPT.Responsive.PMax.MMAV2MSNSingleCarousel,
    EPT.Responsive.PMax.MMAV2MSNRestoday,
    EPT.Responsive.PMax.MMAV2MSNPartnerStripe,
    EPT.Responsive.PMax.MMAV2MSEdgeCarousel,
  ],
}, {
  placementSetName: AdPreviewSurface.Bing,
  placementTypes: [
    EPT.Responsive.PMax.MMALandscapeImage,
    EPT.Responsive.BingSearch.Text,
  ],
}, {
  placementSetName: AdPreviewSurface.Outlook,
  placementTypes: [
    EPT.Outlook.Mobile, EPT.Outlook.Web,
  ],
}];

export const getResponsiveAdPreviewConfig = (isAdPreviewRefreshEnabled, isPMaxAspectRatiosV2Enabled, permissions) => {
  let config = responsiveAdPreviewConfig;
  if (isAdPreviewRefreshEnabled && isPMaxAspectRatiosV2Enabled) {
    config = responsiveAdPreviewConfigV3;
  } else if (isPMaxAspectRatiosV2Enabled) {
    config = responsiveAdPreviewConfigV2;
  }

  if (permissions.dynamic.PMaxVideo) {
    // update Msn placment with video
    const msnSurfacePlacements = config.find(placement => placement.placementSetName === AdPreviewSurface.Msn);

    if (msnSurfacePlacements && !msnSurfacePlacements.placementTypes.includes(EPT.PMax.Video)) {
      msnSurfacePlacements.placementTypes.push(EPT.PMax.Video);
    }

    // if config doesn't have VideoGaming surface, add it
    const videoGamingSurface = config.find(placement => placement.placmentSetName === AdPreviewSurface.VideoGaming);
    if (!videoGamingSurface) {
      config.push({
        placementSetName: AdPreviewSurface.VideoGaming,
        placementTypes: [
          EPT.PMax.Video,
        ],
      });
    }
  }

  return config;
};


export const getExtraPlacementInfo = hasLogo => ({
  [EPT.Responsive.PMax.AudienceWideImage]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('MSN feed'),
    customClassName: 'msan_191x100',
    imagePreviewHeight: AdPreviewOverideStylesByKey.msan_191x100.imagePreviewHeight,
  },
  [EPT.Responsive.PMax.MSEdgeSingleCarousel]: {
    aspectRatioUIString: '1.78:1',
    previewTitle: _TL_('Microsoft Edge single carousel'),
    customClassName: 'edge_178x100',
    imagePreviewHeight: AdPreviewOverideStylesByKey.edge_178x100.imagePreviewHeight,
  },
  [EPT.Responsive.PMax.MSNInArticle]: {
    aspectRatioUIString: '1.5:1',
    previewTitle: _TL_('MSN in-article ad'),
    customClassName: 'msan_150x100',
    imagePreviewHeight: AdPreviewOverideStylesByKey.msan_150x100.imagePreviewHeight,
  },
  [EPT.Responsive.PMax.AudienceSquareImage]: {
    aspectRatioUIString: '1:1',
    previewTitle: _TL_('MSN square image'),
    customClassName: 'msan_1x1',
  },
  [EPT.Responsive.PMax.MSNSmallHeadline]: {
    aspectRatioUIString: '1.33:1',
    previewTitle: _TL_('MSN small headline'),
    customClassName: 'msan_133x100',
  },
  [EPT.Responsive.PMax.MMAWideImage]: {
    aspectRatioUIString: '4:1',
    previewTitle: _TL_('Wide image'),
    customClassName: 'mma_4x1',
  },
  [EPT.Responsive.PMax.MMAVerticalImage]: {
    aspectRatioUIString: '1:2',
    previewTitle: _TL_('Vertical image'),
    customClassName: 'mma_1x2',
    imagePreviewHeight: AdPreviewOverideStylesByKey.mma_1x2.imagePreviewHeight,
  },
  [EPT.Responsive.PMax.MMALandscapeImage]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('Landscape Image Multimedia Ad'),
    customClassName: 'mma_191x100',
    imagePreviewHeight: AdPreviewOverideStylesByKey.mma_191x100.imagePreviewHeight,
    hasLogo,
  },
  [EPT.Responsive.BingSearch.Text]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Bing search ad'),
    customClassName: 'bing_text',
    hasLogo,
  },
  [EPT.Responsive.Outlook.Mobile]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (mobile)'),
    customClassName: 'outlook_text_mobile',
    hasLogo,
  },
  [EPT.Responsive.Outlook.Web]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (computer)'),
    customClassName: 'outlook_text_web',
    hasLogo,
  },
  [EPT.UnifiedAppAds.VideoHorizontal]: {
    aspectRatioUIString: '16:9',
    previewTitle: _TL_('Horizontal fullscreen video'),
    customClassName: 'video_16x9',
  },
  [EPT.UnifiedAppAds.M365Outlook]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Outlook Mobile'),
    customClassName: 'outlook_text_mobile',
    hasLogo,
  },
  [EPT.UnifiedAppAds.MSNDisplay]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('MSN Landscape Image'),
  },
  [EPT.UnifiedAppAds.CasualGamesDimensions]: {
    aspectRatioUIString: '1:1',
    previewTitle: _TL_('Square image'),
  },
  [EPT.Responsive.PMax.MMAV2MSEdgeCarousel]: {
    aspectRatioUIString: '2.01:1',
    previewTitle: _TL_('Microsoft Edge carousel'),
    customClassName: 'edge_201x100',
    hasLogo: true,
  },
  [EPT.Responsive.PMax.MMAV2MSNSingleCarousel]: {
    aspectRatioUIString: '1.69:1',
    previewTitle: _TL_('MSN Single Carousel'),
    customClassName: 'mma_169x100',
  },
  [EPT.Responsive.PMax.MMAV2MSNRestoday]: {
    aspectRatioUIString: '1.24:1',
    previewTitle: _TL_('MSN Restoday'),
    customClassName: 'mma_124x100',
  },
  [EPT.Responsive.PMax.MMAV2MSNPartnerStripe]: {
    aspectRatioUIString: '1.53:1',
    previewTitle: _TL_('MSN Partner Stripe'),
    customClassName: 'mma_153x100',
  },
  [EPT.Responsive.PMax.MMAV2MSNRiver]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('MSN feed'),
    hasLogo: true,
  },
  [EPT.Responsive.PMax.MSEdgeMobile]: {
    aspectRatioUIString: '1:1',
    previewTitle: _TL_('Microsoft Edge mobile'),
    hasLogo: true,
  },
  [EPT.PMax.Video]: {
    previewTitle: _TL_('Video'),
    customClassName: 'video',
  },
  [EPT.Outlook.Mobile]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (mobile)'),
    customClassName: 'outlook_text_mobile',
    hasLogo,
  },
  [EPT.Outlook.Web]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (computer)'),
    customClassName: 'outlook_text_web',
    hasLogo,
  },
});
